import { DailyNavRecord } from 'api/models/DailyNavRecord';

export const DAILY_NAV_SPREADSHEET_NAME = 'daily-nav-spreadsheet';
export const DAILY_NAV_SPREADSHEET_TITLE = 'Company';
export const DAILY_NAV_SPREADSHEET_TITLE_ALIAS = 'title';
export const DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS = 'sections';

export const DAILY_NAV_COLUMN_TYPES = {
  VALUATION_DATE: 'valuation_date',
  EQUITY_VALUE: 'equity_value',
  PRICE_PER_SHARE: 'price_per_share',
  ONE_DAY_DELTA: 'one_day_delta',
  FIVE_DAY_DELTA: 'five_day_delta',
  SINCE_LAST_VALUATION_DELTA: 'since_last_valuation_delta',
  STATUS: 'status',
} as const;

export const DAILY_NAV_COLUMN_ORDER = [
  DAILY_NAV_COLUMN_TYPES.VALUATION_DATE,
  DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE,
  DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE,
  DAILY_NAV_COLUMN_TYPES.ONE_DAY_DELTA,
  DAILY_NAV_COLUMN_TYPES.FIVE_DAY_DELTA,
  DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA,
  DAILY_NAV_COLUMN_TYPES.STATUS,
];

export const DAILY_NAV_COLUMN_LABELS: Record<string, string> = {
  [DAILY_NAV_COLUMN_TYPES.VALUATION_DATE]: 'Valuation Date',
  [DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE]: 'Equity Value',
  [DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE]: 'Price / Share',
  [DAILY_NAV_COLUMN_TYPES.ONE_DAY_DELTA]: '1-Day Δ',
  [DAILY_NAV_COLUMN_TYPES.FIVE_DAY_DELTA]: '5-Day Δ',
  [DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA]: 'Last Val Δ',
  [DAILY_NAV_COLUMN_TYPES.STATUS]: 'Status',
};

// Column-group bands rendered above the column labels, mirroring the monitoring
// prototype's PREVIOUS VALUATION / NAV CONSIDERATIONS sections.
export interface DailyNavSection {
  label: string;
  columnTypes: string[];
}

export const DAILY_NAV_SECTIONS: DailyNavSection[] = [
  {
    label: 'Previous Valuation',
    columnTypes: [
      DAILY_NAV_COLUMN_TYPES.VALUATION_DATE,
      DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE,
      DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE,
    ],
  },
  // Public-comp market-cap deltas: signed percent change of the average of the
  // company config's comps, sourced from Alexandria (1-day, 5-day, and since the
  // last valuation).
  {
    label: 'Public Comp Market Cap',
    columnTypes: [
      DAILY_NAV_COLUMN_TYPES.ONE_DAY_DELTA,
      DAILY_NAV_COLUMN_TYPES.FIVE_DAY_DELTA,
      DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA,
    ],
  },
  {
    label: 'NAV Considerations',
    columnTypes: [DAILY_NAV_COLUMN_TYPES.STATUS],
  },
];

// The Review Price warning is active when a CapIQ delta window has breached its
// effective alert threshold — computed server-side into cap_iq_delta_exceeded —
// and the price has not been marked adjusted. Marking the price adjusted in the
// Review Price dialog clears the warning; otherwise it stays lit. The Status
// column's button styling keys off this.
export const dailyNavRecordNeedsReview = (record: DailyNavRecord): boolean =>
  Boolean(record.cap_iq_delta_exceeded) && !record.price_adjusted;

export const REVIEW_PRICE_LABEL = 'Review Price';

// Report-date query param. Deliberately distinct from the shared 'date' param, which
// Intelligence's measurement-date filter owns; NAV dates are free-standing daily values.
export const DAILY_NAV_REPORT_DATE_PARAM = 'nav_date';
